import moment from "moment/moment";

/**
 * Formats the price with a specified coin symbol and default error message.
 * @param {number|string} price - The price to be formatted.
 * @param {string} [coinSymbol=""] - The symbol of the coin to be appended to the formatted price.
 * @param {string} [defaultError="--"] - The default error message.
 * @param {boolean} [alwaysShowDecimals=true] - If true, always show decimal places
 * @param {number} [decimalPlaces=2] - The number of decimal places to show.
 * @returns {string} The formatted price with the coin symbol.
 * @example formatPrice(1000, "USD", "--") // "1.000,00 USD"
 */
export const formatPrice = (
    price,
    coinSymbol = "",
    defaultError = "--",
    alwaysShowDecimals = true,
    decimalPlaces = 2,
    coinPosition = "end"
) => {
    if (price === null || isNaN(price)) return defaultError;
    price = typeof price === "string" ? price.replace(",", ".") : price.toString().replace(",", ".");

    //split decimal and integer from price
    const priceNonDecimal = price.split(".")[0];
    let priceDecimal = price.split(".")[1] || "";

    //fill the priceDecimal with 0s if it's less than decimalPlaces characters
    if (alwaysShowDecimals) {
        if (!priceDecimal) priceDecimal = "0".repeat(decimalPlaces);
        else if (priceDecimal.length < decimalPlaces) priceDecimal += "0".repeat(decimalPlaces - priceDecimal.length);
        else if (priceDecimal.length > decimalPlaces) priceDecimal = priceDecimal.slice(0, decimalPlaces);
    } else if (priceDecimal && priceDecimal.length > decimalPlaces) {
        priceDecimal = priceDecimal.slice(0, decimalPlaces);
    }

    //format the price to use a dot as a thousand separator
    let formattedPrice = priceNonDecimal.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    //if there is a decimal, add it to the formatted price
    formattedPrice = priceDecimal ? formattedPrice + "," + priceDecimal : formattedPrice;

    return coinSymbol
        ? coinPosition === "end"
            ? formattedPrice + " " + coinSymbol
            : coinSymbol + " " + formattedPrice
        : formattedPrice;
};

/**
 * Formats the percentage with a percent sign and default error message.
 * @param {number|string} percentage - The percentage to be formatted.
 * @param {string} [defaultError="--"] - The default error message.
 * @param {boolean} [alwaysShowDecimals=true] - If true, always show decimal places.
 * @param {number} [decimalPlaces=2] - The number of decimal places to show.
 * @returns {string} The formatted percentage with the percent sign.
 * @example formatPercentage(50.5, "--") // "50,50%"
 */
export const formatPercentage = (percentage, defaultError = "--", alwaysShowDecimals = true, decimalPlaces = 2) => {
    if (percentage === null || isNaN(percentage)) return defaultError;
    percentage =
        typeof percentage === "string" ? percentage.replace(",", ".") : percentage.toString().replace(",", ".");

    const percentageNonDecimal = percentage.split(".")[0];
    let percentageDecimal = percentage.split(".")[1] || "";

    if (alwaysShowDecimals) {
        if (!percentageDecimal) percentageDecimal = "0".repeat(decimalPlaces);
        else if (percentageDecimal.length < decimalPlaces)
            percentageDecimal += "0".repeat(decimalPlaces - percentageDecimal.length);
        else if (percentageDecimal.length > decimalPlaces)
            percentageDecimal = percentageDecimal.slice(0, decimalPlaces);
    } else if (percentageDecimal && percentageDecimal.length > decimalPlaces) {
        percentageDecimal = percentageDecimal.slice(0, decimalPlaces);
    }

    let formattedPercentage = percentageNonDecimal.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    formattedPercentage = percentageDecimal ? formattedPercentage + "," + percentageDecimal : formattedPercentage;

    return formattedPercentage + "%";
};

/**
 * Formats the integer with a default error message.
 * @param {number|string} value - The integer to be formatted.
 * @param {string} [defaultValue="--"] - The default error message.
 * @param {boolean} [alwaysShowDecimals=false] - If true, always show decimal places
 * @param {number} [decimalPlaces=2] - The number of decimal places to show.
 * @returns {string} The formatted integer.
 * @example formatInteger(1000, "--") // "1.000"
 */
export const formatInteger = (value, defaultValue = "--", alwaysShowDecimals = false, decimalPlaces = 2) => {
    if (value === null || isNaN(value)) return defaultValue;

    return formatPrice(value, "", defaultValue, alwaysShowDecimals, decimalPlaces);
};

export const formatPersonFullName = (firstName, lastName, defaultValue = "--") => {
    let result = defaultValue;
    if (firstName && lastName) result = firstName + " " + lastName;
    else if (firstName) result = firstName;
    else if (lastName) result = lastName;
    else result = defaultValue;

    return result;
};

export const formatDefaultDateTime = (date, defaultError = "--") => {
    try {
        if (!date || date === "-") return defaultError;
        const res = moment(date)?.format("DD/MM/YYYY HH:mm") ?? defaultError;
        if (res === "Invalid date") return defaultError;
        return res;
    } catch (error) {
        console.error("formatDefaultDateTime", date);
        return defaultError;
    }
};

export const formatDefaultDate = (date, defaultError = "--") => {
    try {
        if (!date || date === "-") return defaultError;
        const res = moment(date)?.format("DD/MM/YYYY") ?? defaultError;
        if (res === "Invalid date") return defaultError;
        return res;
    } catch (error) {
        console.error("formatDefaultDateTime", date);
        return defaultError;
    }
};

export const formatDateByFormat = (date, format = "DD/MM/YYYY", defaultError = "--") => {
    try {
        if (!date || date === "-") return defaultError;
        const res = moment(date)?.format(format) ?? defaultError;
        if (res === "Invalid date") return defaultError;
        return res;
    } catch (error) {
        console.error("formatDateByFormat", date);
        return defaultError;
    }
};

export const formatDefaultDateTimeISO = (date, defaultError = "--") => {
    try {
        if (!date || date === "-") return defaultError;
        const res = moment(date)?.toISOString() ?? defaultError;
        if (res === "Invalid date") return defaultError;
        return res;
    } catch (error) {
        console.error("formatDefaultDateTime", date);
        return defaultError;
    }
};

/**
 * Formats a color hex value with a default error message.
 * @param {string} color - The color hex value to be formatted.
 * @param {string} [defaultError="#000000"] - The default error message to be returned if the color is invalid or empty.
 * @returns {string} The formatted color hex value.
 * @example formatColorHex("FF0000", "#000000") // "#FF0000"
 */
export const formatColorHex = (color, defaultError = "") => {
    try {
        if (!color) return defaultError;
        if (color.startsWith("#")) return color;
        return "#" + color;
    } catch (error) {
        console.error(`formatColorHex error for color: ${color}`);
        return defaultError;
    }
};

/**
 * Formats and compares two colors.
 *
 * @param {string} color1 - The first color to format and compare.
 * @param {string} color2 - The second color to format and compare.
 * @returns {boolean} Returns true if the formatted colors are equal, false otherwise.
 */
export const formatAndCompareColors = (color1, color2) => {
    const formattedColor1 = formatColorHex(color1?.toUpperCase());
    const formattedColor2 = formatColorHex(color2?.toUpperCase());

    return formattedColor1 === formattedColor2;
};

/**
 * Formats a phone number with a default error message.
 *
 * @param {string} countryPhoneCode - The country phone code to be used for formatting.
 * @param {string} phoneNumber - The phone number to be formatted.
 * @returns {string} The formatted phone number.
 * @example formatPhoneNumber("47", "12345678") // "123 45 678"
 */
export const formatPhoneNumber = (countryPhoneCode, phoneNumber) => {
    if (!phoneNumber) return "";
    const tmp = phoneNumber.replace(/\s/g, "").substring(0, 8);
    const countryPhoneCodeWithoutPlus = countryPhoneCode.replace("+", "");

    //if country is NO (47) then ### ## ### else #### ####
    if (countryPhoneCodeWithoutPlus === "47") {
        if (tmp.length > 5) {
            return tmp.replace(/^(\d{3})(\d{2})(\d+)$/, "$1 $2 $3");
        } else if (tmp.length > 3) {
            return tmp.replace(/^(\d{3})(\d+)$/, "$1 $2");
        }
        return tmp;
    } else {
        if (tmp.length > 8) {
            return tmp.replace(/^(\d{4})(\d{4})(\d+)$/, "$1 $2 $3");
        } else if (tmp.length > 4) {
            return tmp.replace(/^(\d{4})(\d+)$/, "$1 $2");
        }
        return tmp;
    }
};
